/**
 * Wizard setup — the data-shape row: stat windows (+ custom dates), layer
 * toggles and the generation ceilings (max formulas / max rules, shown per
 * target). The AI may return fewer than a ceiling, never more.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Layers } from 'lucide-react';
import type { FlowLayer } from '@trafficpulse/shared';

import { Input } from '@/components/ui/form/input';
import { MultiSearchableSelect } from '@/components/ui/form/input/multi-searchable-select/MultiSearchableSelect';
import { STATS_DATE_RANGE_PRESETS } from '@/components/date-range/dateRangePresets';

import type { AiWizardState } from '../use-ai-wizard';

const LAYERS: FlowLayer[] = ['paths', 'landers', 'offers'];

const CountInput: FC<{
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
}> = ({ label, value, max, onChange }) => (
  <label className="block space-y-1 text-xs font-medium">
    {label} <span className="font-normal text-muted-foreground">(max {max})</span>
    <Input
      type="number"
      min={1}
      max={max}
      value={value}
      onChange={(e) => {
        const parsed = Number.parseInt(e.target.value, 10);
        if (!Number.isNaN(parsed)) {
          onChange(Math.min(max, Math.max(1, parsed)));
        }
      }}
      className="mt-1 h-8 w-24 text-xs"
    />
  </label>
);

const SetupControlsRow: FC<{ wizard: AiWizardState }> = ({ wizard }) => {
  const { options, patch } = wizard;
  const toggleLayer = (layer: FlowLayer) => {
    const on = options.layers.includes(layer);
    const next = on
      ? options.layers.filter((l) => l !== layer)
      : [...options.layers, layer];
    if (next.length) patch({ layers: next });
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="block space-y-1 text-xs font-medium">
        Stat windows{' '}
        <span className="font-normal text-muted-foreground">
          (the AI scores its groups over these)
        </span>
        <span className="mt-1 block w-56">
          <MultiSearchableSelect
            options={STATS_DATE_RANGE_PRESETS.map((preset) => ({
              value: preset.code,
              label: preset.label,
            }))}
            value={options.dateRanges}
            onChange={(dateRanges) =>
              dateRanges.length > 0 && patch({ dateRanges })
            }
            placeholder="Pick stat windows…"
            summaryNoun="windows"
          />
        </span>
      </label>
      {options.dateRanges.includes('custom') ? (
        <>
          <label className="block space-y-1 text-xs font-medium">
            From
            <Input
              type="date"
              value={options.from ?? ''}
              onChange={(e) => patch({ from: e.target.value })}
              className="mt-1 h-8 text-xs"
            />
          </label>
          <label className="block space-y-1 text-xs font-medium">
            To
            <Input
              type="date"
              value={options.to ?? ''}
              onChange={(e) => patch({ to: e.target.value })}
              className="mt-1 h-8 text-xs"
            />
          </label>
        </>
      ) : null}
      {options.target !== 'rules' ? (
        <CountInput
          label="Max formulas"
          value={options.maxFormulas}
          max={8}
          onChange={(maxFormulas) => patch({ maxFormulas })}
        />
      ) : null}
      {options.target !== 'formulas' ? (
        <CountInput
          label="Max rules"
          value={options.maxRules}
          max={10}
          onChange={(maxRules) => patch({ maxRules })}
        />
      ) : null}
      <span className="flex items-center gap-1.5 pb-2">
        <Layers className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        {LAYERS.map((layer) => (
          <button
            key={layer}
            type="button"
            onClick={() => toggleLayer(layer)}
            className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
              options.layers.includes(layer)
                ? 'border-primary/50 bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            {layer}
          </button>
        ))}
      </span>
    </div>
  );
};

export default SetupControlsRow;
